import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { resolveAppSpace, resolveRouteTransitionDirection, resolveRouteTransitionKey, type AppSpace, type RouteTransitionDirection } from '@/features/chat/chat-space-navigation';

// Module scope: the layout swaps its shell between chat and other pages, which remounts the
// outlet, so the space shown before the navigation cannot live in component state alone.
let lastRenderedAppSpace: AppSpace | null = null;

export function AnimatedOutlet({ context }: { context?: unknown }) {
  const location = useLocation();
  const isAuthRoute = location.pathname.startsWith('/auth/');
  const routeTransitionKey = resolveRouteTransitionKey(location.pathname, location.search);
  const appSpace = resolveAppSpace(location.pathname);
  const [routeTransition, setRouteTransition] = useState<{
    direction: RouteTransitionDirection;
    key: string;
    space: AppSpace | null;
  }>(() => ({
    direction: resolveRouteTransitionDirection(lastRenderedAppSpace, appSpace),
    key: routeTransitionKey,
    space: appSpace,
  }));

  if (routeTransition.key !== routeTransitionKey) {
    setRouteTransition({
      direction: resolveRouteTransitionDirection(routeTransition.space, appSpace),
      key: routeTransitionKey,
      space: appSpace,
    });
  }

  useEffect(() => {
    lastRenderedAppSpace = appSpace;
  }, [appSpace]);

  return (
    <div
      key={routeTransitionKey}
      className={cn(
        'flex min-w-0 w-full flex-1 flex-col',
        !isAuthRoute && 'route-transition-enter',
      )}
      data-route-transition={routeTransition.direction}
    >
      <Outlet context={context} />
    </div>
  );
}

